import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import {
  ResumePDFLink,
  ResumePDFText,
} from "components/Resume/ResumePDF/common";
import { useHighlight, shouldHighlightText, isHighlightTarget, getMergedHighlightStyle } from "components/Resume/HighlightContext";
import { useMistakeAnalysis, isWordMistake, isSentenceMistake, isSingleWordMistake } from "components/Resume/MistakeAnalysisContext";
import { handleClick } from "lib/utils/navigator";

const MISTAKE_TEXT_COLOR = "#dc2626";

export const TemplateSeventeenPDFSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) =>{
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  return  (
  <div
  onClick={handleClickCallback}
    style={{
      ...styles.flexCol,
      gap: spacing["2"],
      marginTop: spacing["5"],
      cursor:"pointer"
      // ...style,
    }}
  >
    {heading && (
      <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
        <Text
          style={{
            fontWeight: "bold",
            letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
            color: "black",
          }}
          debug={DEBUG_RESUME_PDF_FLAG}
        >
          {heading}
        </Text>
        {themeColor && (
          <View
            style={{
              height: "1.25pt",
              width: "100%",
              backgroundColor: "white",
              marginRight: spacing["3.5"],
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          />
        )}
      </View>
    )}
    {children}
  </div>
)}

export const TemplateSeventeenProfileSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  return (
    <View
      style={{
        ...styles.flexRow,
        gap: spacing["4"],
        marginTop: spacing["5"],
        ...style
      }}
    >
      {heading && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"],
            width: "20vw",
            textAlign: "right",
          }}
        >
          <Text
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
              color: themeColor || DEFAULT_FONT_COLOR,
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
        </View>
      )}
      <View
        style={{
          ...styles.flexCol,
          gap: spacing[1.5],
          width: "80vw",
        }}
      >
        {children}
      </View>
    </View>
  );
};

export const ResumePDFDescriptionWithWhiteFont = ({
  items,
  enableHighlight = false,
  enableMistakeHighlight = false,
  section = 'education',
  itemIndex = 0,
}: {
  items: string[];
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
  section?: string;
  itemIndex?: number | null;
}) => {
  const mistakeAnalysis = useMistakeAnalysis();
  
  // Check if items is defined and has at least one element
  if (!items || items.length === 0) {
    return null; // or render a placeholder, like <p>No descriptions available</p>
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>{enableHighlight ? renderNodeWithHighlight(node, section, itemIndex, "white", enableMistakeHighlight, mistakeAnalysis) : renderNode(node)}</React.Fragment>
      ))}
    </View>
  );
};

const parseAndTransformHTML = (htmlString: string): React.ReactNode[] => {
  // Create a temporary DOM element to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];

  // Recursive function to process and transform HTML nodes
  const transformNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      switch (element.nodeName.toLowerCase()) {
        case "strong":
          return (
            <ResumePDFText key={Math.random()} bold={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "em":
          return (
            <ResumePDFText key={Math.random()} italic={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "u":
          return (
            <ResumePDFText
              key={Math.random()}
              underline={true}
            >
              {element.textContent}
            </ResumePDFText>
          );
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {element.textContent}
              </ResumePDFText>
            </ResumePDFLink>
          );
        case "span":
          // Skip span elements
          return null;
        case "p":
          return (
            <View key={Math.random()}>
              <ResumePDFText>
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
              ;
            </View>
          );
        case "li":
          return (
            <View key={Math.random()} style={{ flexDirection: "row" }}>
              <ResumePDFText
                style={{
                  paddingLeft: 4, // adjust spacing as needed
                  paddingRight: 4, // adjust spacing as needed
                  lineHeight: 1.3,
                }}
                bold={true}
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText
                style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
              >
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
            </View>
          );
        case "ol":
          return (
            <View key={Math.random()} style={{ flexDirection: "column" }}>
              {Array.from(element.childNodes).map((child, index) =>
                transformNode(child)
              )}
            </View>
          );
        default:
          return null;
      }
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    return null;
  };

  Array.from(doc.body.childNodes).forEach((child) => {
    const transformedNode = transformNode(child);
    if (transformedNode) {
      transformedChildren.push(transformedNode);
    }
  });

  return transformedChildren;
};

const renderNode = (node: Node, themeColor: string = "black"): React.ReactNode => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    switch (element.nodeName.toLowerCase()) {
      case "p":
        return (
          <View>
            <ResumePDFText
              style={{ lineHeight: 1.3, flexGrow: 1 }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
              <br />
            </ResumePDFText>
          </View>
        );
        
      case "ol":
        return (
          <View style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                      lineHeight: 1.3,
                    }}
                    bold={true}
                    themeColor={themeColor}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText
                    style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                    themeColor={themeColor}
                  >
                    {parseAndTransformHTML((child as Element).innerHTML)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
        
      case "li":
        return (
          <View style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor={themeColor}
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
              }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
            </ResumePDFText>
          </View>
        );
        
      default:
        return null;
    }
  }
  return null;
};


export const extractNodesFromHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  return nodes;
};

/**
 * Render node with highlighting support - preserves theme colors
 */
const renderNodeWithHighlight = (
  node: Node,
  currentSection: string,
  currentItemIndex: number | null,
  themeColor: string = "black",
  enableMistakeHighlight: boolean = false,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>
): React.ReactNode => {
  const highlight = useHighlight();
  
  const isTarget = isHighlightTarget(currentSection, currentItemIndex, highlight);
  
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    const textContent = element.textContent || "";
    const nodeName = element.nodeName.toLowerCase();
    
    // Check sentence mistake
    const isSentenceError = enableMistakeHighlight && mistakeAnalysis?.enabled && 
      isSentenceMistake(textContent, currentSection, currentItemIndex, mistakeAnalysis.mistakeData);
    
    const shouldHighlight = isTarget && 
      highlight.enabled && 
      highlight.mode === 'sentence' && 
      shouldHighlightText(textContent, highlight.texts, highlight.mode);
    
    switch (nodeName) {
      case "p":
        return (
          <View key={Math.random()}>
            <ResumePDFText 
              style={{ 
                lineHeight: 1.3,
                flexGrow: 1,
                ...(shouldHighlight ? {
                  backgroundColor: highlight.style.backgroundColor,
                  color: isSentenceError ? MISTAKE_TEXT_COLOR : (highlight.style.color || highlight.style.textDecorationColor),
                  fontWeight: highlight.style.fontWeight as any,
                  textDecoration: highlight.style.textDecoration as any,
                  textDecorationStyle: highlight.style.textDecorationStyle as any,
                  textDecorationColor: highlight.style.textDecorationColor as any,
                } : isSentenceError ? {
                  color: MISTAKE_TEXT_COLOR
                } : {})
              }}
              themeColor={themeColor}
            >
              {parseAndTransformHTMLWithHighlight(element.innerHTML, currentSection, currentItemIndex, themeColor, enableMistakeHighlight, mistakeAnalysis)}
              <br />
            </ResumePDFText>
          </View>
        );
        
      case "ol":
        return (
          <View key={Math.random()} style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                      lineHeight: 1.3,
                    }}
                    bold={true}
                    themeColor={themeColor}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }} themeColor={themeColor}>
                    {parseAndTransformHTMLWithHighlight((child as Element).innerHTML, currentSection, currentItemIndex, themeColor, enableMistakeHighlight, mistakeAnalysis)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
        
      case "li":
        const liShouldHighlight = isTarget && 
          highlight.enabled && 
          highlight.mode === 'sentence' && 
          shouldHighlightText(textContent, highlight.texts, highlight.mode);
          
        return (
          <View key={Math.random()} style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor={themeColor}
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
                ...(liShouldHighlight ? {
                  backgroundColor: highlight.style.backgroundColor,
                  color: isSentenceError ? MISTAKE_TEXT_COLOR : (highlight.style.color || highlight.style.textDecorationColor),
                  fontWeight: highlight.style.fontWeight as any,
                  textDecoration: highlight.style.textDecoration as any,
                  textDecorationStyle: highlight.style.textDecorationStyle as any,
                  textDecorationColor: highlight.style.textDecorationColor as any,
                } : isSentenceError ? {
                  color: MISTAKE_TEXT_COLOR
                } : {} )
              }}
              themeColor={themeColor}
            >
              {parseAndTransformHTMLWithHighlight(element.innerHTML, currentSection, currentItemIndex, themeColor, enableMistakeHighlight, mistakeAnalysis)}
            </ResumePDFText>
          </View>
        );
        
      default:
        return null;
    }
  }
  return null;
};

/**
 * Parse and transform HTML with highlighting support - preserves theme colors
 */
/**
 * Parse and transform HTML with highlighting support - preserves theme colors
 * Enhanced with mistake highlighting support
 */
const parseAndTransformHTMLWithHighlight = (
  htmlString: string,
  currentSection: string,
  currentItemIndex: number | null,
  themeColor: string = "black",
  enableMistakeHighlight: boolean = false,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>
): React.ReactNode[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];
  const highlight = useHighlight();

  const isTarget = isHighlightTarget(currentSection, currentItemIndex, highlight);

  const transformNode = (node: Node, existingStyles: { bold?: boolean; italic?: boolean; underline?: boolean } = {}) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const nodeName = element.nodeName.toLowerCase();

      let newStyles = { ...existingStyles };
      
      switch (nodeName) {
        case "strong":
        case "b":
          newStyles.bold = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "em":
        case "i":
          newStyles.italic = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "u":
          newStyles.underline = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {Array.from(element.childNodes).map((child, idx) => (
                  <React.Fragment key={`${Math.random()}-${idx}`}>
                    {transformNode(child, { ...newStyles, underline: true })}
                  </React.Fragment>
                ))}
              </ResumePDFText>
            </ResumePDFLink>
          );
          
        case "span":
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        default:
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      
      if (!text.trim()) return text;
      
      // Check if this is a sentence mistake
      const isSentenceError = enableMistakeHighlight && mistakeAnalysis?.enabled && 
        isSentenceMistake(text, currentSection, currentItemIndex, mistakeAnalysis.mistakeData);
      
      if (isSentenceError) {
        // Sentence mistake case
        if (isTarget && highlight.enabled && highlight.mode === 'word' && highlight.texts.length > 0) {
          return renderTextWithWordHighlights(text, highlight, existingStyles, themeColor, currentSection, currentItemIndex, mistakeAnalysis, isTarget, enableMistakeHighlight, true);
        }
        
        if (isTarget && highlight.enabled && highlight.mode === 'sentence' && highlight.texts.length > 0) {
          const shouldHighlight = shouldHighlightText(text, highlight.texts, 'sentence');
          if (shouldHighlight) {
            const mergedStyle = getMergedHighlightStyle(existingStyles, highlight.style, true);
            mergedStyle.color = MISTAKE_TEXT_COLOR;
            
            return (
              <ResumePDFText
                key={Math.random()}
                style={mergedStyle}
                themeColor={themeColor}
              >
                {text}
              </ResumePDFText>
            );
          }
        }
        
        const redStyle = existingStyles.bold || existingStyles.italic || existingStyles.underline;
        if (redStyle) {
          return (
            <ResumePDFText
              key={Math.random()}
              bold={existingStyles.bold}
              italic={existingStyles.italic}
              underline={existingStyles.underline}
              style={{ color: MISTAKE_TEXT_COLOR }}
              themeColor={themeColor}
            >
              {text}
            </ResumePDFText>
          );
        }
        return (
          <ResumePDFText key={Math.random()} style={{ color: MISTAKE_TEXT_COLOR }} themeColor={themeColor}>
            {text}
          </ResumePDFText>
        );
      }
      
      // Not a sentence mistake - check if word processing needed
      const needsWordProcessing = 
        (isTarget && highlight.enabled && highlight.mode === 'word' && highlight.texts.length > 0) ||
        (enableMistakeHighlight && mistakeAnalysis?.enabled);
      
      if (needsWordProcessing) {
        return renderTextWithWordHighlights(text, highlight, existingStyles, themeColor, currentSection, currentItemIndex, mistakeAnalysis, isTarget, enableMistakeHighlight, false);
      }

      if (isTarget && highlight.enabled && highlight.mode === 'sentence' && highlight.texts.length > 0) {
        const shouldHighlight = shouldHighlightText(text, highlight.texts, 'sentence');
        const mergedStyle = getMergedHighlightStyle(existingStyles, highlight.style, shouldHighlight);
        
        return (
          <ResumePDFText
            key={Math.random()}
            style={mergedStyle}
            themeColor={themeColor}
          >
            {text}
          </ResumePDFText>
        );
      }

      if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
            themeColor={themeColor}
          >
            {text}
          </ResumePDFText>
        );
      }
      
      return text;
    }
    
    return null;
  };

  doc.body.childNodes.forEach((node) => {
    const transformed = transformNode(node);
    if (transformed) {
      transformedChildren.push(transformed);
    }
  });

  return transformedChildren;
};

/**
 * Render text with word-level highlights - preserves theme colors
 */
/**
 * Render text with word-level highlights - preserves theme colors
 * Enhanced with mistake highlighting support
 */
const renderTextWithWordHighlights = (
  text: string,
  highlight: ReturnType<typeof useHighlight>,
  existingStyles: { bold?: boolean; italic?: boolean; underline?: boolean },
  themeColor: string = "black",
  currentSection?: string,
  currentItemIndex?: number | null,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>,
  isHoverTarget: boolean = false,
  enableMistakeHighlight: boolean = false,
  forceRedText: boolean = false
): React.ReactNode => {
  try {
    const shouldCheckHover = isHoverTarget && highlight.enabled && highlight.mode === 'word' && highlight.texts.length > 0;
    const shouldCheckMistakes = enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && currentSection;
    
    const words = shouldCheckHover
      ? highlight.texts
          .map(w => (w || "").trim())
          .filter(Boolean)
          .map(w => {
            const escaped = w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            return escaped;
          })
          .join("|")
      : "";

    if (!words && !shouldCheckMistakes && !forceRedText) {
      if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
            themeColor={themeColor}
          >
            {text}
          </ResumePDFText>
        );
      }
      return text;
    }

    // Split by hover regex 
    const parts: Array<{ text: string; isHoverMatch: boolean }> = [];
    
    if (words) {
      const re = new RegExp(`\\b(${words})\\b`, "giu");
      let lastIndex = 0;
      let m: RegExpExecArray | null;
      
      while ((m = re.exec(text)) !== null) {
        const matchStart = m.index;
        const matchStr = m[0];
        
        if (matchStart > lastIndex) {
          parts.push({ text: text.substring(lastIndex, matchStart), isHoverMatch: false });
        }
        
        parts.push({ text: matchStr, isHoverMatch: true });
        lastIndex = matchStart + matchStr.length;
      }
      
      if (lastIndex < text.length) {
        parts.push({ text: text.substring(lastIndex), isHoverMatch: false });
      }
      
      if (parts.length === 0) {
        parts.push({ text, isHoverMatch: false });
      }
    } else {
      parts.push({ text, isHoverMatch: false });
    }

    // For each part, check if it's a mistake word
    const finalParts: Array<{ text: string; isHoverMatch: boolean; isMistake: boolean }> = [];
    
    for (const part of parts) {
      if (shouldCheckMistakes && !part.isHoverMatch && part.text.trim()) {
        const wordBoundaryParts = part.text.split(/(\s+)/);
        
        for (const wordPart of wordBoundaryParts) {
          if (!wordPart) continue;
          
          const trimmed = wordPart.trim();
          const isMistake = trimmed.length > 0 && isSingleWordMistake(
            trimmed,
            currentSection!,
            currentItemIndex ?? null,
            mistakeAnalysis!.mistakeData
          );
          
          finalParts.push({
            text: wordPart,
            isHoverMatch: false,
            isMistake
          });
        }
      } else if (shouldCheckMistakes && part.isHoverMatch) {
        const isMistake = isSingleWordMistake(
          part.text.trim(),
          currentSection!,
          currentItemIndex ?? null,
          mistakeAnalysis!.mistakeData
        );
        
        finalParts.push({
          text: part.text,
          isHoverMatch: true,
          isMistake
        });
      } else {
        finalParts.push({
          text: part.text,
          isHoverMatch: part.isHoverMatch,
          isMistake: false
        });
      }
    }

    // Render all parts with appropriate styling
    return (
      <>
        {finalParts.map((part, i) => {
          const isRed = forceRedText || part.isMistake;
          
          if (part.isHoverMatch) {
            const mergedStyle = getMergedHighlightStyle(existingStyles, highlight.style, true);
            if (isRed) {
              mergedStyle.color = MISTAKE_TEXT_COLOR;
            }
            return (
              <ResumePDFText
                key={`${Math.random()}-${i}`}
                style={mergedStyle}
                themeColor={themeColor}
              >
                {part.text}
              </ResumePDFText>
            );
          } else {
            if (isRed || existingStyles.bold || existingStyles.italic || existingStyles.underline) {
              return (
                <ResumePDFText
                  key={`${Math.random()}-${i}`}
                  bold={existingStyles.bold}
                  italic={existingStyles.italic}
                  underline={existingStyles.underline}
                  style={isRed ? { color: MISTAKE_TEXT_COLOR } : {}}
                  themeColor={themeColor}
                >
                  {part.text}
                </ResumePDFText>
              );
            }
            return <React.Fragment key={`${Math.random()}-${i}`}>{part.text}</React.Fragment>;
          }
        })}
      </>
    );
  } catch (error) {
    const fallbackRed = forceRedText ? { color: MISTAKE_TEXT_COLOR } : {};
    if (existingStyles.bold || existingStyles.italic || existingStyles.underline || forceRedText) {
      return (
        <ResumePDFText
          key={Math.random()}
          bold={existingStyles.bold}
          italic={existingStyles.italic}
          underline={existingStyles.underline}
          style={fallbackRed}
          themeColor={themeColor}
        >
          {text}
        </ResumePDFText>
      );
    }
    return text;
  }
};

export const ResumePDFDescription = ({ 
  items,
  enableHighlight = false,
  enableMistakeHighlight = false,
  section = 'experience',
  itemIndex = 0,
  themeColor = "black"
}: { 
  items: string[];
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
  section?: string;
  itemIndex?: number | null;
  themeColor?: string;
}) => {
  const mistakeAnalysis = useMistakeAnalysis();
  
  if (!items || items.length === 0) {
    return null;
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>
          {enableHighlight ? renderNodeWithHighlight(node, section, itemIndex, themeColor, enableMistakeHighlight, mistakeAnalysis) : renderNode(node, themeColor)}
        </React.Fragment>
      ))}
    </View>
  );
};

// Export optimized section components
export { TemplateSeventeenSectionWithSubsections, TemplateSeventeenSection } from './sections';
